import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { has, isEmpty } from 'lodash';

import Image from '../../components/Image';
import RichTextDisplay from '../../components/RichTextDisplay';

import TrackPlayerSlideComponent from '../../hocs/TrackPlayerSlideComponent';

import VerbText from '../../components/VerbText';

const sizeConfig = {
    default: 128,
    thumbnail: 36,
    small: 64,
    medium: 128,
    large: 256
};

class ThisOrThatPermutationSlide extends Component {
    static propTypes = {
        completedData: PropTypes.object,
        onChange: PropTypes.func,
        content: PropTypes.shape({
            questions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
            description: PropTypes.string,
            displayType: PropTypes.string
        }),
        name: PropTypes.string.isRequired,
        canMoveToNext: PropTypes.func,
        currentUser: PropTypes.object.isRequired,
        nextSectionName: PropTypes.string,
        isRequired: PropTypes.bool,
        isUpdatingUserTrack: PropTypes.bool,
        onNext: PropTypes.func,
        sendWorkspaceSegementEvent: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.state = {
            currentQuestionIdx: 0
        };
    }

    componentDidMount() {
        const { canMoveToNext, onChange, isRequired = false, completedData = {} } = this.props;

        if (canMoveToNext) {
            let _canMoveToNext = false;
            if (has(completedData, 'data') && completedData.data !== null) {
                const answersLength = Object.keys(completedData.data).length;
                const {
                    content: { questions }
                } = this.props;
                _canMoveToNext = questions.length === answersLength;
            }
            canMoveToNext(_canMoveToNext || !isRequired);
        }

        if (!isRequired && isEmpty(completedData)) {
            const isCompleted = true;

            const isAnswered = false;
            onChange(null, isCompleted, isAnswered);
        }
    }

    _handleQuestionSelect = (idx, questionId, answerCategoryId = '', text = '') => {
        const {
            content: { questions },
            completedData = {},
            isUpdatingUserTrack,
            onChange,
            canMoveToNext,
            onNext
        } = this.props;
        const changedData = {
            ...completedData.data,
            [questionId]: answerCategoryId,
            text
        };

        // Is complete and answered if the user answers any one prompt
        const hasCompletedData = !isEmpty(changedData);
        const isCompleted = questions.length === idx + 1;
        const isAnswered = hasCompletedData;

        onChange(changedData, hasCompletedData, isAnswered);
        if (isCompleted && !isUpdatingUserTrack) {
            canMoveToNext(true);
            onNext();
        } else {
            this.setState({
                currentQuestionIdx: idx + 1
            });
        }
    };

    /**
     *
     * @param {string} questionId
     * @param {string} answerCategoryId
     * @return {boolean}
     * @private
     */
    _isSelected = (questionId, answerCategoryId, text) => {
        const { completedData = {} } = this.props;

        if (completedData.data && completedData.data.text) {
            return (
                has(completedData, `data.${questionId}`) &&
                completedData.data[questionId] === answerCategoryId &&
                completedData.data.text === text
            );
        }
        return has(completedData, `data.${questionId}`) && completedData.data[questionId] === answerCategoryId;
    };

    /**
     *
     * @param imageOptionClassName
     * @param {number} index
     * @param {string} questionOption  Must be A or B
     * @param {Object} question
     * @return {XML}
     * @private
     */
    _buildImageOption = (imageOptionClassName, index, questionOption, question) => (
        <div
            className={cns(imageOptionClassName, {
                'thisOrThatPermutationSlide-option-selected': this._isSelected(
                    question.id,
                    question[questionOption].categoryId,
                    question[questionOption].text
                )
            })}
            onClick={() =>
                this._handleQuestionSelect(
                    index,
                    question.id,
                    question[questionOption].categoryId,
                    question[questionOption].text
                )
            }
            onKeyPress={() =>
                this._handleQuestionSelect(
                    index,
                    question.id,
                    question[questionOption].categoryId,
                    question[questionOption].text
                )
            }
            tabIndex={'0'}
        >
            <Image
                src={question[questionOption].image}
                className={'thisOrThatPermutationSlide-optionImage'}
                alt={`Option ${index} - ${questionOption}`}
                dimension={{ w: sizeConfig.large, h: sizeConfig.large }}
                style={{ width: sizeConfig.large, height: sizeConfig.large }}
            />
        </div>
    );

    _buildImageOptionAnswers = (imageOptionClassName, index, answerIndex, question) => (
        <div
            className={cns(imageOptionClassName, {
                'thisOrThatPermutationSlide-option-selected': this._isSelected(
                    question.id,
                    question.answers[answerIndex].categoryId,
                    question.answers[answerIndex].text
                )
            })}
            onClick={() =>
                this._handleQuestionSelect(
                    index,
                    question.id,
                    question.answers[answerIndex].categoryId,
                    question.answers[answerIndex].text
                )
            }
            onKeyPress={() =>
                this._handleQuestionSelect(
                    index,
                    question.id,
                    question.answers[answerIndex].categoryId,
                    question.answers[answerIndex].text
                )
            }
            tabIndex={'0'}
        >
            <Image
                src={question.answers[answerIndex].image}
                className={'thisOrThatPermutationSlide-optionImage'}
                alt={`Option ${index} - ${answerIndex}`}
                dimension={{ w: sizeConfig.large, h: sizeConfig.large }}
                style={{ width: sizeConfig.large, height: sizeConfig.large }}
            />
        </div>
    );

    /**
     *
     * @param radioOptionClassName
     * @param {number} index
     * @param {string} questionOption
     * @param {Object} question
     * @return {XML}
     * @private
     */
    _buildRadioOption = (radioOptionClassName, index, questionOption, question) => (
        <div className={'thisOrThatPermutationSlide-optionRadioContainer'}>
            <div
                onClick={() =>
                    this._handleQuestionSelect(
                        index,
                        question.id,
                        question[questionOption].categoryId,
                        question[questionOption].text
                    )
                }
                className={cns(radioOptionClassName, {
                    'thisOrThatPermutationSlide-option-selected': this._isSelected(
                        question.id,
                        question[questionOption].categoryId,
                        question[questionOption].text
                    )
                })}
            >
                <div
                    className={'thisOrThatPermutationSlide-optionRadio'}
                    onKeyPress={() =>
                        this._handleQuestionSelect(
                            index,
                            question.id,
                            question[questionOption].categoryId,
                            question[questionOption].text
                        )
                    }
                    tabIndex={'0'}
                />
                <div className={'thisOrThatPermutationSlide-optionLabel'}>{question[questionOption].text}</div>
            </div>
        </div>
    );

    _buildRadioOptionAnswers = (radioOptionClassName, index, answerIndex, question) => (
        <div key={answerIndex} className={'thisOrThatPermutationSlide-optionRadioContainer'}>
            <div
                onClick={() =>
                    this._handleQuestionSelect(
                        index,
                        question.id,
                        question.answers[answerIndex].categoryId,
                        question.answers[answerIndex].text
                    )
                }
                className={cns(radioOptionClassName, {
                    'thisOrThatPermutationSlide-option-selected': this._isSelected(
                        question.id,
                        question.answers[answerIndex].categoryId,
                        question.answers[answerIndex].text
                    )
                })}
            >
                <div
                    className={'thisOrThatPermutationSlide-optionRadio'}
                    onKeyPress={() =>
                        this._handleQuestionSelect(
                            index,
                            question.id,
                            question.answers[answerIndex].categoryId,
                            question.answers[answerIndex].text
                        )
                    }
                    tabIndex={'0'}
                />
                <div className={'thisOrThatPermutationSlide-optionLabel'}>{question.answers[answerIndex].text}</div>
            </div>
        </div>
    );

    getCurrentQuestion = (question, idx) => {
        const {
            content: { displayType }
        } = this.props;
        const { currentQuestionIdx } = this.state;
        if (idx !== currentQuestionIdx) {
            return null;
        }

        const hasImage = displayType === 'image';

        const className = cns('thisOrThatPermutationSlide-option', {
            'thisOrThatPermutationSlide-option-hasImage': hasImage
        });
        let displayElements;
        if (question.A && question.B) {
            if (hasImage) {
                displayElements = (
                    <div className={'thisOrThatPermutationSlide-options'}>
                        {this._buildImageOption(className, idx, 'A', question)}
                        {this._buildImageOption(className, idx, 'B', question)}
                    </div>
                );
            } else {
                displayElements = (
                    <div>
                        {this._buildRadioOption(className, idx, 'A', question)}
                        {this._buildRadioOption(className, idx, 'B', question)}
                    </div>
                );
            }
        }
        if (question.answers && question.answers.length) {
            const answers = [];
            if (displayType === 'image') {
                for (let i = 0; i < question.answers.length; i += 1) {
                    answers.push(this._buildImageOptionAnswers(className, idx, i, question));
                }
            } else {
                for (let i = 0; i < question.answers.length; i += 1) {
                    answers.push(this._buildRadioOptionAnswers(className, idx, i, question));
                }
            }
            displayElements = <div>{answers}</div>;
        }

        return (
            <div key={idx} className={'thisOrThatPermutationSlide-questionPrompt'}>
                <h4 className={'thisOrThatPermutationSlide-questionPromptHeader'}>{question.prompt}</h4>
                <div className={'thisOrThatPermutationSlide-questionPromptContent'}>{displayElements}</div>
                <br />
            </div>
        );
    };

    render() {
        const {
            currentUser,
            name,
            nextSectionName,
            content: { questions = [], description = '' },
            sendWorkspaceSegementEvent
        } = this.props;

        const { currentQuestionIdx } = this.state;

        const currentQuestion = currentQuestionIdx + 1;
        const totalQuestionCount = questions.length;

        return (
            <div className={'thisOrThatPermutationSlide'}>
                <h1 className={'verb-title-1 thisOrThatPermutationSlide-title playerHeader'}>
                    {name}
                    <span className={'thisOrThatPermutationSlide-progress'}>
                        <VerbText
                            messageId={'WORKSPACE_THIS_OR_THAT_PROGRESS'}
                            defaultMessage={'{currentQuestionNumber} of {totalQuestionNumber}'}
                            valuesToInject={{
                                currentQuestionNumber: currentQuestion,
                                totalQuestionNumber: totalQuestionCount
                            }}
                        />
                    </span>
                </h1>
                <div className={'thisOrThatPermutationSlide-questions playerContent'}>
                    <RichTextDisplay
                        className={'playerDescription'}
                        htmlToDisplay={description}
                        currentUser={currentUser}
                        nextSectionName={nextSectionName}
                    />
                    {questions.map(this.getCurrentQuestion)}
                </div>
            </div>
        );
    }
}

export const ThisOrThatPermutationSlideComponent = ThisOrThatPermutationSlide;
export default TrackPlayerSlideComponent(ThisOrThatPermutationSlide);
